import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';

import {
  criarDocumento,
  criarDocumentoBase,
  excluirDocumento,
  excluirDocumentoBase,
  type DocumentoRequerido,
  type DocumentoRequeridoInput,
} from '@/api/secretario';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

/** Escopo do item: específico de uma área, ou base universal da configuração. */
export type EscopoEditor = { tipo: 'area'; areaId: string } | { tipo: 'base' };

interface DocumentosRequeridosEditorProps {
  escopo: EscopoEditor;
  documentos: DocumentoRequerido[];
}

/** Checklist de documentos que o secretário pede ao cliente (§5.1). Itens com
 * condição só são solicitados se a condição se aplicar ao caso. */
export function DocumentosRequeridosEditor({ escopo, documentos }: DocumentosRequeridosEditorProps) {
  const queryClient = useQueryClient();
  const { showError } = useNotification();
  const [novoNome, setNovoNome] = useState('');
  const [novaCondicao, setNovaCondicao] = useState('');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.secretario.config });

  const proximaOrdem = documentos.reduce((max, d) => Math.max(max, d.ordem), -1) + 1;
  const criar = useMutation({
    mutationFn: (payload: DocumentoRequeridoInput) =>
      escopo.tipo === 'area' ? criarDocumento(escopo.areaId, payload) : criarDocumentoBase(payload),
    onSuccess: () => {
      setNovoNome('');
      setNovaCondicao('');
      invalidate();
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  const excluir = useMutation({
    mutationFn: (id: string) =>
      escopo.tipo === 'area' ? excluirDocumento(escopo.areaId, id) : excluirDocumentoBase(id),
    onSuccess: invalidate,
    onError: (error) => notifyMutationError(error, showError),
  });

  const podeAdicionar = !!novoNome.trim() && !criar.isPending;
  const adicionar = () => {
    if (podeAdicionar) {
      criar.mutate({ nome: novoNome.trim(), condicao: novaCondicao.trim() || null, ordem: proximaOrdem });
    }
  };

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium">Documentos solicitados</h4>
      {documentos.length === 0 && <p className="text-xs text-muted-foreground">Nenhum documento configurado.</p>}
      <ul className="space-y-1.5">
        {documentos.map((doc) => (
          <li key={doc.id} className="flex items-start justify-between gap-2 rounded-md border px-3 py-1.5">
            <span className="flex flex-col gap-0.5 text-sm">
              <span className="flex items-center gap-2">
                {doc.nome}
                {doc.obrigatorio && <Badge variant="secondary">Obrigatório</Badge>}
              </span>
              {doc.condicao && <span className="text-xs text-muted-foreground">Só se {doc.condicao}</span>}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => excluir.mutate(doc.id)}
              disabled={excluir.isPending}
              aria-label={`Remover ${doc.nome}`}
            >
              <Trash2 size={16} />
            </Button>
          </li>
        ))}
      </ul>
      <div className="space-y-2">
        <div className="flex gap-2">
          <Input
            value={novoNome}
            onChange={(e) => setNovoNome(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                adicionar();
              }
            }}
            placeholder="Ex.: Documento de identidade com foto"
            maxLength={120}
          />
          <Button type="button" variant="outline" onClick={adicionar} disabled={!podeAdicionar}>
            <Plus size={16} className="mr-1" />
            Adicionar
          </Button>
        </div>
        <Input
          value={novaCondicao}
          onChange={(e) => setNovaCondicao(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              adicionar();
            }
          }}
          placeholder="Condição (opcional) — ex.: se houver menor de idade envolvido"
          maxLength={300}
        />
      </div>
    </div>
  );
}
